import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { notificationService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';

const FIELDS = [
  { key: 'license_warn_days', labelKey: 'ruleLicenseWarn' },
  { key: 'process_stale_days', labelKey: 'ruleProcessStale' },
  { key: 'vistoria_prazo_days', labelKey: 'ruleVistoriaPrazo' },
  { key: 'risk_complaint_density', labelKey: 'ruleRiskDensity' },
  { key: 'risk_recidivism', labelKey: 'ruleRiskRecidivism' },
  { key: 'anomaly_complaint_spike', labelKey: 'ruleAnomalySpike' },
];

const PRAZO_KEYS = [
  { key: 'LP', labelKey: 'rulePrazoLp' },
  { key: 'LI', labelKey: 'rulePrazoLi' },
  { key: 'LO', labelKey: 'rulePrazoLo' },
  { key: 'default', labelKey: 'rulePrazoDefault' },
];

export default function SettingsRulesTab({ headerBar }) {
  const { t } = useTranslation('settings');
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    notificationService
      .getRules()
      .then(({ data }) => {
        if (active) setRules(data?.rules || {});
      })
      .catch((e) => {
        if (active) toast.error(userFacingError(e, 'settings:rulesLoadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const setNum = (key, value) => {
    const n = Number(value);
    setRules((current) => ({ ...current, [key]: Number.isFinite(n) ? n : value }));
  };

  const save = async () => {
    if (!rules) return;
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => {
        if (rules[key] != null && rules[key] !== '') payload[key] = Number(rules[key]);
      });
      const prazo = {};
      PRAZO_KEYS.forEach(({ key }) => {
        const n = Number(rules.license_prazo_days?.[key]);
        if (Number.isFinite(n) && n > 0) prazo[key] = n;
      });
      if (Object.keys(prazo).length) payload.license_prazo_days = prazo;
      const { data } = await notificationService.updateRules(payload);
      setRules(data?.rules || rules);
      toast.success(t('rulesSaved'));
    } catch (e) {
      toast.error(userFacingError(e, 'settings:rulesSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="data-list-card flex items-center justify-center gap-2 p-10 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('rulesLoading')}
      </div>
    );
  }

  return (
    <div className="data-list-card">
      {headerBar(SlidersHorizontal, t('rulesTitle'), t('rulesDescription'))}
      <div className="grid gap-4 p-5 sm:grid-cols-2 sm:p-6">
        {FIELDS.map(({ key, labelKey }) => (
          <div key={key} className="form-group">
            <label className="form-label" htmlFor={`rule-${key}`}>
              {t(labelKey)}
            </label>
            <input
              id={`rule-${key}`}
              type="number"
              min={1}
              className="form-input"
              value={rules?.[key] ?? ''}
              onChange={(e) => setNum(key, e.target.value)}
            />
          </div>
        ))}
        <p className="sm:col-span-2 text-sm font-medium text-gray-800">{t('rulePrazoTitle')}</p>
        {PRAZO_KEYS.map(({ key, labelKey }) => (
          <div key={key} className="form-group">
            <label className="form-label" htmlFor={`rule-prazo-${key}`}>
              {t(labelKey)}
            </label>
            <input
              id={`rule-prazo-${key}`}
              type="number"
              min={1}
              className="form-input"
              value={rules?.license_prazo_days?.[key] ?? ''}
              onChange={(e) => {
                const n = Number(e.target.value);
                setRules((current) => ({
                  ...current,
                  license_prazo_days: {
                    ...(current?.license_prazo_days || {}),
                    [key]: Number.isFinite(n) ? n : e.target.value,
                  },
                }));
              }}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? t('rulesSaving') : t('rulesSave')}
          </button>
        </div>
      </div>
    </div>
  );
}
